import { useEffect, useState, type ReactNode } from 'react';
import { LanguageContext } from './LanguageContext';
import { translations, defaultLocale, supportedLocales, type Locale } from './translations';

const STORAGE_KEY = 'scalent-locale-v1';

function detectBrowserLocale(): Locale {
  const lang = navigator.language?.toLowerCase() || '';
  return supportedLocales.find((l) => lang === l || lang.startsWith(`${l}-`)) || defaultLocale;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && supportedLocales.includes(stored as Locale)) return stored as Locale;
    } catch {}
    return detectBrowserLocale();
  });

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = (next: Locale) => {
    if (!supportedLocales.includes(next)) return;
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {}
  };

  const t = translations[locale] as Record<string, any>;

  return (
    <LanguageContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </LanguageContext.Provider>
  );
}
